import { SelectHTMLAttributes, forwardRef } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'name'> {
  name: string;
  label: string;
  options: SelectOption[];
  error?: string;
  register?: UseFormRegisterReturn;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      name,
      label,
      options,
      error,
      register,
      placeholder,
      className = '',
      required,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'block w-full px-4 py-3 border-3 text-sm font-body transition-all duration-200 focus:outline-none focus:border-4 focus:-translate-y-1 focus:shadow-[4px_4px_0px_0px_rgba(0,60,102,0.3)] disabled:opacity-50 disabled:cursor-not-allowed bg-white';

    const stateClasses = error
      ? 'border-amaranth_purple-500 text-amaranth_purple-900 focus:border-amaranth_purple-500'
      : 'border-gray-400 text-gray-900 focus:border-indigo_dye-500 hover:bg-gray-50';

    return (
      <div className="w-full">
        <label
          htmlFor={name}
          className="block font-mono text-sm uppercase tracking-wide font-semibold text-gray-900 mb-2"
        >
          {label}
          {required && <span className="text-amaranth_purple-500 ml-1">*</span>}
        </label>
        <select
          id={name}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`${baseClasses} ${stateClasses} ${className}`}
          ref={ref}
          {...register}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p
            id={`${name}-error`}
            className="mt-2 font-mono text-xs uppercase tracking-wide font-bold text-amaranth_purple-600"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
